import { Button } from "components/DesignSystem/Button/Button";
import Input from "components/DesignSystem/Input/Input";
import Modal from "components/DesignSystem/Modal/Modal";
import { FormikForm } from "components/FormikForm/FormikForm";
import { SmallCircleCross } from "components/icons/SmallCircleCross";
import dayjs from "dayjs";
import { formatDate } from "utils/formatDate";
import { object, string } from "yup";
import { FINALLY_REVERT_CONFIRMATION_TEXT } from "./constants";

type RevertFinallyImportModalProps = {
  open: boolean;
  hasImportRun: boolean;
  prevFyEndDate: string | null;
  dateTillImport: string | null;
  isLoading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

const CONFIRMATION_ERROR = `Text must match the following: "${FINALLY_REVERT_CONFIRMATION_TEXT}"`;

const validationSchema = object({
  confirmation: string()
    .oneOf([FINALLY_REVERT_CONFIRMATION_TEXT], CONFIRMATION_ERROR)
    .required(CONFIRMATION_ERROR),
});

const COA_RESET_TEXT = "Your Chart of Accounts is reset to Inkle's default";
const UPLOADS_CLEARED_TEXT = "Your uploaded Finally files are cleared";

export const RevertFinallyImportModal = ({
  open,
  hasImportRun,
  prevFyEndDate,
  dateTillImport,
  isLoading,
  onCancel,
  onConfirm,
}: RevertFinallyImportModalProps) => {
  let deletedEntriesText =
    "You permanently lose the imported transactions and journal entries";

  if (prevFyEndDate && dateTillImport) {
    const fromDate = formatDate(dayjs(prevFyEndDate).add(1, "day"));
    const toDate = formatDate(dateTillImport);
    deletedEntriesText = `You permanently lose the transactions and journal entries imported between ${fromDate} and ${toDate}`;
  }

  let consequences = [UPLOADS_CLEARED_TEXT];

  if (hasImportRun) {
    consequences = [deletedEntriesText, COA_RESET_TEXT, UPLOADS_CLEARED_TEXT];
  }

  return (
    <Modal.Root open={open} onOpenChange={onCancel}>
      <Modal.Content>
        <FormikForm
          initialValues={{ confirmation: "" }}
          validationSchema={validationSchema}
          validateOnMount
          onSubmit={onConfirm}
        >
          {({ isValid }) => (
            <>
              <Modal.Header>
                <Modal.Title>Revert Finally migration</Modal.Title>
                <Modal.Close />
              </Modal.Header>
              <Modal.Body className="t-flex t-flex-col t-gap-4">
                <div className="t-text-body-sm t-text-text-100">
                  Are you sure you want to revert the
                  <b> Finally migration?</b>
                </div>
                <div className="t-flex t-flex-col t-gap-3 t-py-4">
                  <div className="t-text-subtext-sm t-text-text-100">
                    Upon reverting:
                  </div>
                  {consequences.map((consequence) => (
                    <div
                      key={consequence}
                      className="t-flex t-gap-2 t-items-start t-text-body-sm t-text-text-60"
                    >
                      <span className="t-text-red t-flex t-pt-1 t-font-semibold">
                        <SmallCircleCross />
                      </span>
                      {consequence}
                    </div>
                  ))}
                </div>
                <Input
                  withForm
                  hideError
                  required
                  name="confirmation"
                  label={
                    <>
                      Type
                      <span className="t-text-red">
                        {" "}
                        {FINALLY_REVERT_CONFIRMATION_TEXT}{" "}
                      </span>
                      to confirm.
                    </>
                  }
                  placeholder={FINALLY_REVERT_CONFIRMATION_TEXT}
                />
              </Modal.Body>
              <Modal.FooterButtonGroup>
                <Button
                  size="small"
                  type="button"
                  onClick={onCancel}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  type="submit"
                  customType="danger"
                  isLoading={isLoading}
                  disabled={!isValid || isLoading}
                >
                  Revert
                </Button>
              </Modal.FooterButtonGroup>
            </>
          )}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
